import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { onboardingAPI } from '../../services/api';

const lifestyleSchema = z.object({
  bodyComp: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  thinkAboutFood: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  eatDifferently: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  eatWhenNotHungry: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  exerciseExcessively: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  calculateCalories: z.enum(['Yes', 'No'], { required_error: 'Please select an option' })
});

type LifestyleFormData = z.infer<typeof lifestyleSchema>;

interface LifestyleFormProps {
  onComplete: () => void;
}

const LifestyleForm: React.FC<LifestyleFormProps> = ({ onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LifestyleFormData>({
    resolver: zodResolver(lifestyleSchema)
  });

  const onSubmit = async (data: LifestyleFormData) => {
    setIsSubmitting(true);
    try {
      await onboardingAPI.submitLifestyle(data);
      onComplete();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit lifestyle form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RadioGroup = ({ name, question, error }: { name: keyof LifestyleFormData; question: string; error?: string }) => (
    <div className="mb-6">
      <fieldset>
        <legend className="text-sm font-medium text-gray-900 mb-3">
          **{question}
        </legend>
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="Yes"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="No"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">No</span>
          </label>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </fieldset>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Lifestyle Assessment</h1>
          <p className="text-red-600 font-medium">**FIELDS ARE REQUIRED</p>
          <p className="text-gray-600 mt-2">
            These questions help us understand your relationship with food, exercise, and body image to create a healthy, sustainable approach.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <RadioGroup
            name="bodyComp"
            question="Do you think about your body composition (muscle, fat, weight) frequently?"
            error={errors.bodyComp?.message}
          />

          <RadioGroup
            name="thinkAboutFood"
            question="Do you think about food frequently throughout the day?"
            error={errors.thinkAboutFood?.message}
          />

          <RadioGroup
            name="eatDifferently"
            question="Do you eat differently in front of others compared to when you're alone?"
            error={errors.eatDifferently?.message}
          />

          <RadioGroup
            name="eatWhenNotHungry"
            question="Do you eat when you're not hungry?"
            error={errors.eatWhenNotHungry?.message}
          />

          <RadioGroup
            name="exerciseExcessively"
            question="Do you exercise excessively or feel guilty when you miss a workout?"
            error={errors.exerciseExcessively?.message}
          />

          <RadioGroup
            name="calculateCalories"
            question="Do you calculate calories or track your food intake?"
            error={errors.calculateCalories?.message}
          />

          <div className="bg-purple-50 border border-purple-200 rounded-md p-4 mb-6">
            <h3 className="text-sm font-medium text-purple-800 mb-2">Important Note:</h3>
            <p className="text-sm text-purple-700">
              These questions help us understand your mindset and relationship with health and fitness. 
              There are no right or wrong answers - we want to create a program that supports your overall well-being 
              and helps you develop a healthy, sustainable relationship with food and exercise.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Our Approach:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• We focus on sustainable, long-term health rather than quick fixes</li>
              <li>• Your program will be designed to fit your lifestyle and preferences</li>
              <li>• We promote a balanced approach to nutrition and exercise</li>
              <li>• Our goal is to help you develop healthy habits that last</li>
            </ul>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Complete Onboarding >'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LifestyleForm;
